/**
 * TourController - Joyride wrapper component
 * Handles tour rendering, navigation, and step transitions
 * 
 * FIXED VERSION 3:
 * - Removed Stakeholder Map steps
 * - Simplified tooltip display logic
 * - Using run prop to control Joyride visibility
 * - Skip functionality properly calls skipTour
 * - Custom scroll behavior to center elements in viewport
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS, Step, LIFECYCLE } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from './TourProvider';
import { tourSteps, TourStep } from './tourSteps';
import { mobileTourSteps } from './mobileTourSteps';

// HEAT brand colors for tour styling
const joyrideStyles = {
  options: {
    primaryColor: '#ea580c',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    arrowColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
  },
  buttonNext: {
    backgroundColor: '#ea580c',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
  },
  buttonBack: {
    color: '#6b7280',
    marginRight: '10px',
    fontSize: '14px',
  },
  buttonSkip: {
    color: '#6b7280',
    fontSize: '14px',
  },
  tooltip: {
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    padding: '20px',
  },
  tooltipTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
    color: '#111827',
  },
  tooltipContent: {
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#4b5563',
  },
  spotlight: {
    borderRadius: '8px',
  },
};

const TourController: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRunning, currentStep, setCurrentStep, completeTour, skipTour } = useTour();
  const [run, setRun] = useState(false);
  const navigationInProgressRef = useRef(false);
  
  // Detect mobile viewport and select appropriate tour steps
  const isMobile = window.innerWidth < 1024;
  const activeTourSteps = isMobile ? mobileTourSteps : tourSteps;

  // Handle route navigation and target element detection
  useEffect(() => {
    if (!isRunning) {
      setRun(false);
      navigationInProgressRef.current = false;
      return;
    }

    const step = activeTourSteps[currentStep];
    if (!step) {
      console.log('[Tour] No step found for index:', currentStep);
      return;
    }

    const targetRoute = (step as any).route;
    
    // If we need to navigate to a different route
    if (targetRoute && location.pathname !== targetRoute) {
      console.log('[Tour] Navigating to:', targetRoute);
      setRun(false); // Stop tour during navigation
      navigationInProgressRef.current = true;
      navigate(targetRoute);
      return;
    }

    // Wait for target element to be available
    const waitForTarget = () => {
      const targetSelector = step.target as string;
      const targetElement = document.querySelector(targetSelector);
      
      if (targetElement) {
        console.log('[Tour] Target found:', targetSelector);
        
        // Custom scroll to center the element in viewport
        // This prevents sidebar elements from being cut off at the top
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',    // Center vertically in viewport
          inline: 'nearest'   // Don't scroll horizontally
        });
        
        navigationInProgressRef.current = false;
        
        // Wait for scroll animation to complete before showing tooltip
        setTimeout(() => {
          setRun(true);
        }, 400);  // Increased from 100ms to allow smooth scroll animation
      } else {
        console.log('[Tour] Waiting for target:', targetSelector);
        setTimeout(waitForTarget, 300);
      }
    };

    // Start waiting for target after navigation completes
    const timer = setTimeout(waitForTarget, 500);
    return () => clearTimeout(timer);
  }, [isRunning, currentStep, location.pathname, navigate]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type, lifecycle } = data;
    
    console.log('[Tour] Callback:', { status, action, index, type, lifecycle });

    // Handle tour completion
    if (status === STATUS.FINISHED) {
      console.log('[Tour] Finished');
      completeTour();
      return;
    }

    // Handle skip - use skipTour which saves to localStorage
    if (status === STATUS.SKIPPED) {
      console.log('[Tour] Skipped');
      skipTour();
      return;
    }

    // Handle step navigation
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        const nextStep = index + 1;
        if (nextStep < activeTourSteps.length) {
          console.log('[Tour] Moving to next step:', nextStep);
          setRun(false); // Stop tour before navigation
          setCurrentStep(nextStep);
        } else {
          console.log('[Tour] No more steps, completing tour');
          completeTour();
        }
      } else if (action === ACTIONS.PREV) {
        const prevStep = index - 1;
        if (prevStep >= 0) {
          console.log('[Tour] Moving to previous step:', prevStep);
          setRun(false); // Stop tour before navigation
          setCurrentStep(prevStep);
        }
      }
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      console.log('[Tour] Closed');
      skipTour(); // Treat close as skip (saves to localStorage)
    }
  }, [completeTour, skipTour, setCurrentStep]);

  // Don't render if tour is not running
  if (!isRunning) {
    return null;
  }

  return (
    <Joyride
      steps={activeTourSteps as Step[]}
      stepIndex={currentStep}
      run={run}
      continuous
      showProgress
      showSkipButton
      hideCloseButton={false}
      disableOverlayClose
      disableScrolling={true}  // Disable Joyride's automatic scrolling
      scrollToFirstStep={false} // We handle scrolling manually
      spotlightClicks={false}
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish Tour',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      floaterProps={{
        disableAnimation: false,
      }}
    />
  );
};

export default TourController;
